/**
 * Bulk Email API wrapper — E-93 Story 93.1.
 *
 * Mirrors `apps/kgc-api/src/modules/messaging-gateway/dto/list-eligible-partners.dto.ts`
 * and `types/eligible-partner.type.ts`. Keep these two in sync manually (no
 * shared DTO package yet). Contract test candidate for Sprint 9.
 */

import { api } from './client';

export type PartnerSegmentDto = 'VIP' | 'ACTIVE' | 'AT_RISK' | 'INACTIVE';
export type HasActiveRentalFilter = 'yes' | 'no' | 'expiring_7d';
export type LastPurchaseDaysFilter = '30' | '60' | '90_plus';
export type HasOutstandingFilter = 'yes' | 'no';

export interface EligiblePartnerFilters {
  segment?: PartnerSegmentDto[] | undefined;
  hasActiveRental?: HasActiveRentalFilter | undefined;
  lastPurchaseDays?: LastPurchaseDaysFilter | undefined;
  hasOutstanding?: HasOutstandingFilter | undefined;
  hasEmail?: boolean | undefined;
  marketingConsent?: boolean | undefined;
  search?: string | undefined;
  cursor?: string | undefined;
  limit?: number | undefined;
}

export interface EligiblePartner {
  id: string;
  name: string;
  companyName: string | null;
  email: string | null;
  segment: PartnerSegmentDto;
  currentBalance: number;
  hasActiveRental: boolean;
  lastPurchaseAt: string | null;
  marketingConsent: boolean;
  unsubscribed: boolean;
}

export interface EligiblePartnerListResponse {
  data: EligiblePartner[];
  totalCount: number;
  hasMore: boolean;
  nextCursor: string | null;
}

/**
 * Build the query-string for `GET /email/bulk-send/eligible-partners`.
 * Exported so the TanStack Query hook and any adhoc fetchers can share
 * the exact same serialization — a mismatched param name between frontend
 * and backend silently returns the wrong slice.
 */
export function buildEligiblePartnersQuery(filters: EligiblePartnerFilters): string {
  const qs = new URLSearchParams();

  if (filters.segment && filters.segment.length > 0) {
    // The DTO preprocesses both `?segment=A&segment=B` and comma-joined forms.
    // We use the repeated-key form — it's what URLSearchParams emits natively.
    for (const s of filters.segment) qs.append('segment', s);
  }
  if (filters.hasActiveRental) qs.set('hasActiveRental', filters.hasActiveRental);
  if (filters.lastPurchaseDays) qs.set('lastPurchaseDays', filters.lastPurchaseDays);
  if (filters.hasOutstanding) qs.set('hasOutstanding', filters.hasOutstanding);
  if (filters.hasEmail !== undefined) qs.set('hasEmail', String(filters.hasEmail));
  if (filters.marketingConsent !== undefined) {
    qs.set('marketingConsent', String(filters.marketingConsent));
  }
  if (filters.search && filters.search.trim().length > 0) qs.set('search', filters.search.trim());
  if (filters.cursor) qs.set('cursor', filters.cursor);
  if (filters.limit !== undefined) qs.set('limit', String(filters.limit));

  return qs.toString();
}

export async function fetchEligiblePartners(
  filters: EligiblePartnerFilters
): Promise<EligiblePartnerListResponse> {
  const qs = buildEligiblePartnersQuery(filters);
  const res = await api.get<{ data: EligiblePartnerListResponse }>(
    `/email/bulk-send/eligible-partners${qs ? `?${qs}` : ''}`
  );
  return res.data;
}

// ============================================
// E-93 Story 93.8 — GDPR legal basis mirror
// ============================================
//
// Keep this in sync with
// `packages/integration/messaging-gateway/src/interfaces/template-legal-basis.ts`.
// Contract test candidate for Sprint 9 — for now manual parity maintenance.

export type TemplateLegalBasis = 'TRANSACTIONAL' | 'MARKETING_CONSENT' | 'LEGITIMATE_INTEREST';

export type BulkSendSkipReason = 'no_email' | 'not_found' | 'no_consent' | 'unsubscribed';

export interface BulkSendSkipped {
  partnerId: string;
  reason: BulkSendSkipReason;
}

export interface BulkSendError {
  partnerId: string;
  error: string;
}

export interface BulkSendResultDto {
  queued: number;
  skipped: number;
  failed: number;
  skippedDetails: BulkSendSkipped[];
  errors: BulkSendError[];
  legalBasis: TemplateLegalBasis;
}

/**
 * Slug → default legal basis. Matches
 * `TEMPLATE_LEGAL_BASIS_REGISTRY` on the backend. The frontend uses this
 * to render a pre-flight badge so BOLTVEZETO sees which GDPR gate will
 * apply *before* hitting send — the backend remains the source of truth
 * (the send response surfaces the effective basis).
 */
export const TEMPLATE_LEGAL_BASIS_MIRROR: Readonly<Record<string, TemplateLegalBasis>> =
  Object.freeze({
    'invoice-sent': 'TRANSACTIONAL',
    'invoice-overdue': 'LEGITIMATE_INTEREST',
    'rental-confirmation': 'TRANSACTIONAL',
    'rental-overdue': 'LEGITIMATE_INTEREST',
    'rental-deposit-warning': 'LEGITIMATE_INTEREST',
    'worksheet-created': 'TRANSACTIONAL',
    'worksheet-ready': 'TRANSACTIONAL',
    'quote-sent': 'TRANSACTIONAL',
    'welcome-partner': 'TRANSACTIONAL',
    'payment-received': 'TRANSACTIONAL',
  });

/**
 * Pure helper — returns the legal basis for a slug or null if the slug
 * is unknown (i.e. a tenant-admin custom slug that the mirror doesn't
 * know about). Frontend renders an "unknown" badge rather than guessing.
 */
export function legalBasisForSlug(slug: string | null | undefined): TemplateLegalBasis | null {
  if (!slug) return null;
  return TEMPLATE_LEGAL_BASIS_MIRROR[slug] ?? null;
}

// ============================================
// E-93 Story 93.6 — Scheduled batches list + cancel
// ============================================
//
// Mirrors `apps/kgc-api/src/modules/messaging-gateway/services/
// scheduled-batches.service.ts`. Contract test candidate for Sprint 9.

export type BulkEmailBatchStatus = 'PENDING' | 'QUEUED' | 'DISPATCHING' | 'COMPLETED' | 'CANCELLED';

export interface ScheduledBatchRow {
  id: string;
  campaignId: string;
  templateSlug: string;
  language: string;
  status: BulkEmailBatchStatus;
  legalBasis: TemplateLegalBasis;
  totalRecipients: number;
  enqueuedCount: number;
  skippedCount: number;
  scheduledAt: string | null;
  createdAt: string;
}

export interface ListScheduledResponse {
  data: ScheduledBatchRow[];
  totalCount: number;
}

export type CancelOutcome = 'cancelled' | 'already_cancelled' | 'not_cancellable' | 'not_found';

export interface CancelResultDto {
  outcome: CancelOutcome;
  batchId: string;
  status: string;
}

/**
 * Fetch scheduled batches. Defaults to PENDING+QUEUED on the backend
 * — pass `statuses` to widen (e.g. `['CANCELLED']` for a recent-cancel
 * lens).
 */
export async function fetchScheduledBatches(
  opts: {
    statuses?: BulkEmailBatchStatus[];
    limit?: number;
  } = {}
): Promise<ListScheduledResponse> {
  const qs = new URLSearchParams();
  if (opts.statuses && opts.statuses.length > 0) {
    qs.set('status', opts.statuses.join(','));
  }
  if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
  const suffix = qs.toString();
  const res = await api.get<{ data: ListScheduledResponse }>(
    `/email/scheduled${suffix ? `?${suffix}` : ''}`
  );
  return res.data;
}

/**
 * Cancel a scheduled batch by id. Backend maps:
 *   200 → cancelled / already_cancelled (idempotent)
 *   404 → not_found (raised by the backend, surfaces as a fetch error)
 *   409 → not_cancellable (already DISPATCHING / COMPLETED, race-loser)
 *
 * Throws on 4xx — callers should wrap in try/catch and translate the
 * status code to a toast.
 */
export async function cancelScheduledBatch(batchId: string): Promise<CancelResultDto> {
  const res = await api.post<{ data: CancelResultDto }>(`/email/scheduled/${batchId}/cancel`, {});
  return res.data;
}

// ============================================
// E-93 Story 93.3 — KPI stats endpoint
// ============================================

export interface BulkEmailStats {
  totalBatches: number;
  activeCampaigns: number;
  completedToday: number;
  /** null when the window has zero recipients — UI treats as "— %" not 0 %. */
  successRate: number | null;
  totalRecipientsWindow: number;
  windowDays: number;
}

export async function fetchBulkEmailStats(windowDays?: number): Promise<BulkEmailStats> {
  const qs = windowDays !== undefined ? `?windowDays=${windowDays}` : '';
  const res = await api.get<{ data: BulkEmailStats }>(`/email/stats${qs}`);
  return res.data;
}
